import React, {useState} from "react";
import {Link} from "react-router";
import {styled} from "styled-components";
import blue from "@material-ui/core/colors/blue";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import DescriptionIcon from "@material-ui/icons/Description";
import {Typography, Paper} from "@material-ui/core";

import MatterDeleteDialog from "../matter_delete_dialog";
import {Table, Td, Tr} from "common_components/table";
import Th from "common_components/table/th";
import {Matter, MatterId} from "common/types/matter";
import {OrganisationId} from "common/types/organisation";
import {ProjectId} from "common/types/project";
import {formatDate} from "common/utils/format_date";

interface MatterTableProps {
  matters: Matter[];
  organisationId: OrganisationId;
  projectId: ProjectId;
  onDeleteMatter: (matterId: MatterId) => void;
}

const MatterName = styled.span`
  font-weight: 600;
  color: #424242;
  text-decoration: none;

  tr:hover & {
    color: ${blue[500]};
    text-decoration: underline;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyStateContainer = styled(Paper)`
  padding: 3rem 2rem;
  margin: 2rem 0;
  width: 100%;
  box-sizing: border-box;
  text-align: center;
  background-color: #f9fafb;
  border: 2px dashed #e0e0e0;
`;

function MatterTable({
  matters,
  organisationId,
  projectId,
  onDeleteMatter,
}: MatterTableProps) {
  const [matterToDelete, setMatterToDelete] = useState<Matter | null>(null);

  const handleDeleteConfirm = () => {
    if (matterToDelete) {
      setMatterToDelete(null);
      onDeleteMatter(matterToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setMatterToDelete(null);
  };

  if (matters.length === 0) {
    return (
      <EmptyStateContainer elevation={0}>
        <DescriptionIcon
          style={{fontSize: "4rem", color: "#9e9e9e", marginBottom: "1rem"}}
        />
        <Typography
          variant="h5"
          gutterBottom
          style={{fontWeight: 500, color: "#424242"}}
        >
          No contract families yet
        </Typography>
        <Typography variant="body1" style={{color: "#757575"}}>
          Get started by creating your first contract family. Upload MSAs and
          SOWs to analyse your contracts together.
        </Typography>
      </EmptyStateContainer>
    );
  }

  return (
    <>
      <Table isInitiallySorted={false}>
        <thead>
          <tr>
            <Th>
              <span>Name</span>
            </Th>
            <Th>
              <span>Creation Date</span>
            </Th>
            <Th
              style={{width: "100px"}}
              containerStyle={{justifyContent: "center"}}
            >
              <span>Actions</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {matters.map(matter => (
            <Tr
              key={matter.id}
              style={{cursor: "pointer"}}
              hoverStyle={{backgroundColor: "#EFEFF1"}}
            >
              <Td>
                <Link
                  to={`/organisation/${organisationId}/project/${projectId}/matter/${matter.id}`}
                  style={{textDecoration: "none"}}
                >
                  <MatterName>{matter.config.name}</MatterName>
                </Link>
              </Td>
              <Td>{formatDate(new Date(matter.creation_date))}</Td>
              <Td style={{width: "100px", textAlign: "center"}}>
                <ActionsContainer>
                  <Link
                    to={`/organisation/${organisationId}/project/${projectId}/matter/${matter.id}/document`}
                  >
                    <IconButton style={{padding: "8px"}} title="View documents">
                      <DescriptionIcon style={{fontSize: "1.2rem"}} />
                    </IconButton>
                  </Link>
                  <Link
                    to={`/organisation/${organisationId}/project/${projectId}/matter/${matter.id}`}
                  >
                    <IconButton style={{padding: "8px"}} title="Add files">
                      <NoteAddIcon style={{fontSize: "1.2rem"}} />
                    </IconButton>
                  </Link>
                  <IconButton
                    onClick={e => {
                      e.stopPropagation();
                      setMatterToDelete(matter);
                    }}
                    style={{padding: "8px"}}
                    title="Delete contract family"
                  >
                    <DeleteIcon style={{fontSize: "1.2rem"}} />
                  </IconButton>
                </ActionsContainer>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>

      <MatterDeleteDialog
        matter={matterToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}

export default MatterTable;
